import Axios from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { maskCEP } from 'util/masks'
import Select from 'util/modules/Form/Select'
import { createAddress, getAddress, getAddressList, updateAddress } from './redux/actions'

const initialState = {
  alias: '',
  zip: '',
  country: '',
  state: '',
  city: '',
  street: '',
  number: '',
  district: '',
  complement: '',
  latitude: 0,
  longitude: 0
}

const AddressForm: React.FC<{ person?: number }> = ({ person }) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [countries, setCountries] = useState<{ short: string, name: string }[]>([])
  const [states, setStates] = useState<{ short: string, name: string }[]>([])
  const [cities, setCities] = useState<{ name: string }[]>([])

  const [isFetching, setFetching] = useState(false)
  const [isFetchingGeo, setFetchingGeo] = useState({ country: false, state: false, city: false })

  const [form, setForm] = useState(initialState)
  const isFormValid = !Object.values(form).find(v => !v)

  const { personId: routePerson, addressId: id } = useParams<{ personId: string, addressId: string }>()
  const inpRef = useRef<HTMLInputElement>(null)

  const personId = routePerson || person

  async function fetchCountries (): Promise<void> {
    setFetchingGeo({ ...isFetchingGeo, country: true })
    await Axios
      .get('/address/country', { params: { fields: 'short,name' } })
      .then(res => res.data)
      .then(res => {
        if (res?.data) {
          setCountries(res.data.map(({ short, name }: any) => ({ short, name })))
          setStates([])
          setCities([])
        }
      })
      .catch((err) => {
        console.log(`An error occurred when tried to fetch the countries ${err.message}`)
      })

    setFetchingGeo({ ...isFetchingGeo, country: false })
  }

  async function fetchStates (country: string): Promise<void> {
    if (country) {
      setFetchingGeo({ ...isFetchingGeo, state: true })

      await Axios
        .get('/address/state', { params: { fields: 'short,name', 'filter[country.short]': country } })
        .then(res => res.data)
        .then(res => {
          if (res?.data) {
            setStates(res.data.map(({ short, name }: any) => ({ short, name })))
            setCities([])
          }
        })
        .catch((err) => {
          console.log(`An error occurred when tried to fetch the states ${err.message}`)
        })

      setFetchingGeo({ ...isFetchingGeo, state: false })
    }
  }

  async function fetchCities (country: string, state: string): Promise<void> {
    if (country && state) {
      setFetchingGeo({ ...isFetchingGeo, city: true })

      await Axios
        .get('/address/city', {
          params: {
            fields: 'name',
            'filter[country.short]': country,
            'filter[state.short]': state
          }
        })
        .then(res => res.data)
        .then(res => {
          if (res?.data) {
            setCities(res.data.map(({ name }: any) => ({ name })))
          }
        })
        .catch((err) => {
          console.log(`An error occurred when tried to fetch the cities ${err.message}`)
        })

      setFetchingGeo({ ...isFetchingGeo, city: false })
    }
  }

  function fetchZip (zip: string): void {
    if (zip.length === 9) {
      Axios
        .get(`/address/zip/${zip.replace(/[\D]/g, '')}`)
        .then(res => res.data)
        .then(res => {
          if (res?.data) {
            setForm({
              ...form,
              street: res.data.street || form.street,
              number: res.data.number || form.number,
              district: res.data.district || form.district,
              complement: res.data.complement || form.complement,
              state: res.data.city?.state?.short || form.state,
              city: res.data.city?.name || form.city
            })

            if (res.data.city?.state?.short) {
              fetchCities(res.data.city.country.short, res.data.city.state.short)
            }
          }
        })
    }
  }

  function handleClear (): void {
    setForm(initialState)
    history.push(person ? '/panel/profile' : `/panel/person/${personId}/address`)
  }

  async function handleForm (e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setFetching(true)
    if (personId && id) {
      await updateAddress(personId, id, form)(dispatch)
    } else if (personId) {
      await createAddress(personId, form)(dispatch).then(res => {
        if (res?.status === 'success') {
          setForm(initialState)
        }
        return res
      })
    }
    setFetching(false)
  }

  useEffect(() => {
    if (personId) {
      getAddressList(personId)(dispatch)
    }

    fetchCountries()
  }, [dispatch, personId])

  useEffect(() => {
    if (personId && id) {
      getAddress(personId, id)(dispatch).then((res) => {
        if (res?.status === 'success' && res?.data) {
          setForm({
            alias: res.data.alias,
            zip: res.data.zip,
            country: res.data.country,
            state: res.data.state,
            city: res.data.city,
            street: res.data.street,
            number: res.data.number,
            district: res.data.district,
            complement: res.data.complement,
            latitude: res.data.latitude,
            longitude: res.data.longitude
          })
          inpRef.current?.focus()
        }
      })
    } else {
      setForm(initialState)
    }
  }, [dispatch, personId, id, inpRef])

  return (
    <form className="row justify-start form-responsive" onSubmit={handleForm}>
      {
        id
          ? <h2 className="col-12">Edição de Endereço</h2>
          : <h2 className="col-12">Cadastro de Endereço</h2>
      }

      <div className="form-control col-2">
        <label>Apelido para o endereço</label>
        <input
          type="text"
          value={form.alias}
          onChange={(e): void => setForm({ ...form, alias: e.target.value })}
          required
        />
      </div>
      <div className="form-control col-2">
        <label>CEP</label>
        <input
          type="text"
          value={form.zip}
          onChange={(e): void => setForm({ ...form, zip: maskCEP(e.target.value) })}
          onBlur={(e): void => fetchZip(e.target.value)}
          required
        />
      </div>
      <div className="form-control col-2">
        <label>
          País
          { isFetchingGeo.country && <i className="fa fa-spinner fa-spin"/> }
        </label>
        <Select
          required
          selected={form.country}
          disabled={isFetchingGeo.country}
          onSelect={(country): void => {
            if (country) fetchStates(country)
            setForm({ ...form, country, state: '', city: '' })
          }}
          itens={countries.map(({ short, name }) => ({ key: short, value: name }))}
        />
      </div>
      <div className="form-control col-3">
        <label>
          Estado
          {isFetchingGeo.state && <i className="fa fa-spinner fa-spin" />}
        </label>
        <Select
          required
          selected={form.state}
          disabled={isFetchingGeo.state}
          onSelect={(state): void => {
            if (state) fetchCities(form.country, state)
            setForm({ ...form, state, city: '' })
          }}
          itens={states.map(({ short, name }) => ({ key: short, value: name }))}
        />
      </div>
      <div className="form-control col-3">
        <label>
          Cidade
          {isFetchingGeo.city && <i className="fa fa-spinner fa-spin" />}
        </label>
        <Select
          required
          selected={form.city}
          disabled={isFetchingGeo.city}
          onSelect={(city): void => {
            setForm({ ...form, city })
          }}
          itens={cities.map(({ name }) => ({ key: name, value: name }))}
        />
      </div>

      <div className="form-control col-4">
        <label>Logradouro</label>
        <input
          type="text"
          value={form.street}
          onChange={(e): void => setForm({ ...form, street: e.target.value })}
          required
        />
      </div>
      <div className="form-control col-2">
        <label>Número</label>
        <input
          type="text"
          value={form.number}
          onChange={(e): void => setForm({ ...form, number: e.target.value })}
        />
      </div>
      <div className="form-control col-3">
        <label>Setor/Bairro</label>
        <input
          type="text"
          value={form.district}
          onChange={(e): void => setForm({ ...form, district: e.target.value })}
          required
        />
      </div>
      <div className="form-control col-3">
        <label>Complemento</label>
        <input
          type="text"
          value={form.complement}
          onChange={(e): void => setForm({ ...form, complement: e.target.value })}
          required
        />
      </div>

      <div className="form-control justify-end responsive-btn">
        <span
          onClick={handleClear}
          style={{ width: 300 }}
          className="button secondary margin-right-8"
        >
          Limpar
        </span>
        <button className="primary" style={{ width: 300 }} disabled={!isFormValid || isFetching}>
          { isFetching ? 'Processando...' : (id ? 'Atualizar' : 'Cadastrar') }
        </button>
      </div>
    </form>
  )
}

export default AddressForm
